import { TodoActionType } from "models/actionTypes/todoActionTypes";
import { ReduxConstants, ServiceConstants, TodoConstants } from "utils/constants";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { ThunkAction } from "redux-thunk";
import { TodoService, UserService } from "services";
import { AppState } from "store";
import { AuthHelper } from "utils/helpers";

export const setTodoCount = (total: number): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.SET_TODO_COUNT,
    payload: total,
  };
};

export const setTodoDrawerState = (isOpen: boolean): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.SET_TODO_DRAWER_STATE,
    payload: isOpen,
  };
};

export const setTodos = (todos?: WintSuperkollAPIModel.Todo[], total?: number): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.LOAD_TODOS,
    payload: { items: todos, total: total },
  };
};

export const emptyTodos = (): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.EMPTY_TODOS,
  };
};

export const setTodoSummary = (todoSummary?: WintSuperkollAPIModel.TodoSummary): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.LOAD_TODOS_SUMMARY,
    payload: todoSummary,
  };
};

export const emptyTodoSummary = (): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.EMPTY_TODOS_SUMMARY,
  };
};

export const setTodosForUrgency = (
  urgency: WintSuperkollAPIModel.TodoUrgency,
  todos: WintSuperkollAPIModel.Todo[],
  total: number
): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.LOAD_TODOS_FOR_URGENCY,
    payload: { urgency, items: todos, total },
  };
};

export const setTodosForGroup = (
  group: WintSuperkollAPIModel.TodoGroup,
  todos: WintSuperkollAPIModel.Todo[],
  total: number
): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.LOAD_TODOS_FOR_GROUP,
    payload: { group, items: todos, total },
  };
};

export const startLoadTodoSummary = (): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.START_LOAD_TODOS_SUMMARY,
  };
};

export const loadTodoSummaryFailed = (): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.LOAD_TODOS_SUMMARY_FAILED,
  };
};

export const loadTodoSummaryAsync = (companyId: number): ThunkAction<Promise<void>, AppState, null, TodoActionType> => {
  return async (dispatch) => {
    dispatch(startLoadTodoSummary());
    TodoService.getTodoSummary()
      .then((data) => {
        if (AuthHelper.isSelectedCompany(companyId)) dispatch(setTodoSummary(data));
      })
      .catch((e) => {
        if (AuthHelper.isSelectedCompany(companyId)) dispatch(loadTodoSummaryFailed);
      });
  };
};

export const startLoadTodos = (): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.START_LOAD_TODOS,
  };
};

export const loadTodosFailed = (): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.LOAD_TODOS_FAILED,
  };
};

export const loadTodosCountAsync = (companyId: number): ThunkAction<Promise<void>, AppState, null, TodoActionType> => {
  return async (dispatch) => {
    dispatch(startLoadTodos());
    TodoService.getTodos(0)
      .then((data) => {
        if (AuthHelper.isSelectedCompany(companyId)) dispatch(setTodos(data.items ?? [], data.totalItems));
      })
      .catch((e) => {
        if (AuthHelper.isSelectedCompany(companyId)) dispatch(loadTodosFailed());
      });
  };
};

export const loadTodosForUrgencyAsync = (
  urgency: WintSuperkollAPIModel.TodoUrgency,
  total: number
): ThunkAction<Promise<WintUtilsWebApi.ListResponse<WintSuperkollAPIModel.Todo>>, AppState, null, TodoActionType> => {
  return async (dispatch, getState) => {
    const companyId = getState().auth.selectedCompanyId ?? 0;
    const data = await TodoService.getTodosByFilter({
      ...ServiceConstants.DefaultFilter,
      urgency: [urgency],
      types: TodoConstants.SupportingTodoTypes,
      page: 0,
      numPerPage: total + 10,
      excludeSnoozed: true,
    });
    if (AuthHelper.isSelectedCompany(companyId)) {
      dispatch(setTodosForUrgency(urgency, data.result.data.items ?? [], data.result.data.totalItems));
    }

    return data.result.data;
  };
};

export const loadTodosForGroupAsync = (
  group: WintSuperkollAPIModel.TodoGroup,
  total: number
): ThunkAction<Promise<void>, AppState, null, TodoActionType> => {
  return async (dispatch, getState) => {
    const companyId = getState().auth.selectedCompanyId ?? 0;
    const data = await TodoService.getTodosByFilter({
      ...ServiceConstants.DefaultFilter,
      groups: [group],
      types: TodoConstants.SupportingTodoTypes,
      page: 0,
      numPerPage: total + 10,
      excludeSnoozed: true,
    });

    if (AuthHelper.isSelectedCompany(companyId)) {
      dispatch(setTodosForGroup(group, data.result.data.items ?? [], data.result.data.totalItems));
    }
  };
};

export const setLastViewedTab = (tab: WintSuperkollAPIModel.TodoGroupType): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.SET_LAST_VIEWED_TAB,
    payload: tab,
  };
};

export const setLastViewedTodoType = (
  type?: WintSuperkollAPIModel.TodoGroup,
  subTypes?: WintSuperkollAPIModel.TodoSubType[]
): TodoActionType => {
  return {
    type: ReduxConstants.TodoActions.SET_LAST_VIEWED_TYPE,
    payload: type !== undefined ? { group: type, subTypes: subTypes } : undefined,
  };
};

/**
 * @deprecated This Redux action is deprecated. Create a new useLoadLastViewedTab hook instead.
 *
 * Migration guide:
 * 1. Replace loadLastViewedTab() dispatch calls with useLoadLastViewedTab hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle Redux state updates through the hook or custom Redux actions
 *
 * @see useLoadLastViewedTab - The replacement hook (to be created)
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { loadLastViewedTab } from "store/actions/todoActions";
 *
 * dispatch(loadLastViewedTab());
 *
 * @example
 * // New way (recommended):
 * import useLoadLastViewedTab from "serviceHooks/useLoadLastViewedTab";
 *
 * function MyComponent() {
 *   const { loadLastViewedTab } = useLoadLastViewedTab();
 *   loadLastViewedTab();
 * }
 */
export const loadLastViewedTab = (): ThunkAction<Promise<void>, AppState, null, TodoActionType> => {
  return async (dispatch) => {
    UserService.getDefaultTodoGroupType().then((result) => {
      dispatch(setLastViewedTab(result.data));
    });
  };
};

export const updateLastViewedTab = (
  tab: WintSuperkollAPIModel.TodoGroupType
): ThunkAction<Promise<void>, AppState, null, TodoActionType> => {
  return async (dispatch) => {
    UserService.updateDefaultTodoGroupType(tab);
    dispatch(setLastViewedTab(tab));
  };
};
